/**
 * Cadena de integridad independiente por device_id (ADR OQ-1, condicion 2;
 * INV-9). Sin secuencia global: cada dispositivo extiende su propia cadena
 * sin coordinarse con los demas, lo que permite converger sin sobrescribir ni
 * reordenar (INV-6, FR-SYNC-4).
 *
 * Alcance de amenaza (INV-9, H2): detecta alteracion y eliminacion externas
 * NO COORDINADAS, comparadas con el manifiesto de extremos de al menos una
 * replica local confiable. NO detecta ni pretende detectar la alteracion
 * coordinada simultanea de la hoja y todos los manifiestos locales — eso
 * exigiria un ancla independiente fuera del alcance de este nucleo (ADR OQ-2:
 * un cliente publico no puede custodiar el secreto que eso requeriria).
 */

import type { Entry } from "../core/entry.js";
import { canonicalizeEntry, type ChainedEntryContent } from "./canonical.js";
import { computeChainRoot, computeEntryHash, computeNextChainHash } from "./hash.js";

export interface ChainedEntry {
  readonly entry: Entry;
  readonly deviceId: string;
  readonly deviceSeq: number;
  readonly createdAt: string;
  readonly entryHash: string;
  readonly deviceChainHash: string;
}

export interface DeviceChainState {
  readonly deviceId: string;
  readonly lastSeq: number; // 0 = cadena vacia
  readonly lastChainHash: string; // raiz si lastSeq === 0
}

function contentOf(chained: Pick<ChainedEntry, "entry" | "deviceId" | "deviceSeq" | "createdAt">): ChainedEntryContent {
  return {
    entryId: chained.entry.entryId,
    deviceId: chained.deviceId,
    deviceSeq: chained.deviceSeq,
    date: chained.entry.date,
    description: chained.entry.description,
    origin: chained.entry.origin,
    reversesEntryId: chained.entry.reversesEntryId,
    createdAt: chained.createdAt,
    postings: chained.entry.postings,
  };
}

export async function initDeviceChain(bookId: string, deviceId: string): Promise<DeviceChainState> {
  return { deviceId, lastSeq: 0, lastChainHash: await computeChainRoot(bookId, deviceId) };
}

/** Anexa un asiento a la cadena de un dispositivo. Nunca reescribe ni reordena entradas previas. */
export async function appendChainedEntry(
  entry: Entry,
  priorState: DeviceChainState,
  createdAt: string,
): Promise<{ chained: ChainedEntry; nextState: DeviceChainState }> {
  const deviceSeq = priorState.lastSeq + 1;
  const entryHash = await computeEntryHash(
    canonicalizeEntry(contentOf({ entry, deviceId: priorState.deviceId, deviceSeq, createdAt })),
  );
  const deviceChainHash = await computeNextChainHash(priorState.lastChainHash, entryHash);
  const chained: ChainedEntry = {
    entry,
    deviceId: priorState.deviceId,
    deviceSeq,
    createdAt,
    entryHash,
    deviceChainHash,
  };
  return {
    chained,
    nextState: { deviceId: priorState.deviceId, lastSeq: deviceSeq, lastChainHash: deviceChainHash },
  };
}

/** Lo que una replica recuerda haber observado de un dispositivo (nunca vive en la hoja: solo local). */
export interface ExtentManifestEntry {
  readonly deviceId: string;
  readonly lastSeqObserved: number;
  readonly lastChainHashObserved: string;
  readonly observedAt: string;
}

export type ChainIssue =
  | { readonly kind: "altered"; readonly entryId: string; readonly deviceSeq: number }
  | { readonly kind: "gap"; readonly fromSeq: number; readonly toSeq: number }
  | { readonly kind: "extent_regression"; readonly observedSeq: number; readonly foundSeq: number };

export interface ChainVerificationReport {
  readonly deviceId: string;
  /** Mayor deviceSeq verificado como parte de una cadena intacta desde la raiz. */
  readonly validUpToSeq: number;
  readonly issues: readonly ChainIssue[];
  readonly recomputedChainHashAtValidUpToSeq: string;
}

/**
 * Verifica la cadena completa de un dispositivo contra la raiz y, si existe,
 * contra el manifiesto de extremos de la replica. `entries` debe contener
 * solo asientos de un mismo deviceId; no necesita venir ordenado.
 */
export async function verifyDeviceChain(
  bookId: string,
  deviceId: string,
  entries: readonly ChainedEntry[],
  manifest: ExtentManifestEntry | undefined,
): Promise<ChainVerificationReport> {
  for (const e of entries) {
    if (e.deviceId !== deviceId) {
      throw new RangeError(`verifyDeviceChain recibio un asiento de otro dispositivo: ${e.deviceId}`);
    }
  }

  const sorted = [...entries].sort((a, b) => a.deviceSeq - b.deviceSeq);
  const issues: ChainIssue[] = [];
  let runningHash = await computeChainRoot(bookId, deviceId);
  let validUpToSeq = 0;
  let expectedSeq = 1;

  for (const e of sorted) {
    if (e.deviceSeq !== expectedSeq) {
      issues.push({ kind: "gap", fromSeq: expectedSeq, toSeq: e.deviceSeq });
      break; // no se puede seguir verificando la cadena mas alla de un hueco
    }
    const recomputedEntryHash = await computeEntryHash(canonicalizeEntry(contentOf(e)));
    if (recomputedEntryHash !== e.entryHash) {
      issues.push({ kind: "altered", entryId: e.entry.entryId, deviceSeq: e.deviceSeq });
      break; // la cadena a partir de aqui ya no es verificable con confianza
    }
    const recomputedChainHash = await computeNextChainHash(runningHash, recomputedEntryHash);
    if (recomputedChainHash !== e.deviceChainHash) {
      issues.push({ kind: "altered", entryId: e.entry.entryId, deviceSeq: e.deviceSeq });
      break;
    }
    runningHash = recomputedChainHash;
    validUpToSeq = e.deviceSeq;
    expectedSeq += 1;
  }

  if (manifest && manifest.deviceId === deviceId) {
    const regressed =
      validUpToSeq < manifest.lastSeqObserved ||
      (validUpToSeq === manifest.lastSeqObserved && runningHash !== manifest.lastChainHashObserved);
    if (regressed) {
      issues.push({
        kind: "extent_regression",
        observedSeq: manifest.lastSeqObserved,
        foundSeq: validUpToSeq,
      });
    }
  }

  return { deviceId, validUpToSeq, issues, recomputedChainHashAtValidUpToSeq: runningHash };
}

/** entryId de todo asiento que debe quedar en cuarentena segun el reporte (FR-STORE-13). */
export function quarantinedEntryIds(
  entries: readonly ChainedEntry[],
  report: ChainVerificationReport,
): readonly string[] {
  return entries
    .filter((e) => e.deviceId === report.deviceId && e.deviceSeq > report.validUpToSeq)
    .map((e) => e.entry.entryId);
}

export function nextManifestEntry(report: ChainVerificationReport, observedAt: string): ExtentManifestEntry {
  return {
    deviceId: report.deviceId,
    lastSeqObserved: report.validUpToSeq,
    lastChainHashObserved: report.recomputedChainHashAtValidUpToSeq,
    observedAt,
  };
}
